import clsx from 'clsx';
import {
  flattenConnection,
  Image,
  Link,
  Money,
  useMoney,
} from '@shopify/hydrogen';

import {Text} from '~/components';
import {isDiscounted, isNewArrival} from '~/lib/utils';
import {getProductPlaceholder} from '~/lib/placeholders';
import {slogans} from '~/Data';

export function ProductCard({product, label, className, loading, onClick, reverseOrder=false}) {
  let cardLabel;

  const cardData = product?.variants ? product : getProductPlaceholder();

  const {
    image,
    priceV2: price,
    compareAtPriceV2: compareAtPrice,
  } = flattenConnection(cardData?.variants)[0] || {};

  if (label) {
    cardLabel = label;
  } else if (isDiscounted(price, compareAtPrice)) {
    cardLabel = 'Sale';
  } else if (isNewArrival(product.publishedAt)) {
    cardLabel = 'New';
  }

  const styles = clsx('gap-8 w-full max-w-3xl mx-auto bg-gray-100 rounded-xl p-8 flex justify-center ' + (reverseOrder ? 'flex-col md:flex-row-reverse' : 'flex-col md:flex-row') + ' ', className);

  return (
    <div className={styles}>
      <div className="card-image w-full aspect-[4/3] md:w-1/2">
        <Text
          as="label"
          size="fine"
          className="absolute top-0 right-0 m-4 text-right text-notice"
        >
          {cardLabel}
        </Text>
        {image && (
          <Image
            className="aspect-[4/3] w-full object-fit fadeIn"
            widths={[400]}
            sizes="400px"
            loaderOptions={{
              crop: 'center',
              scale: 2,
              width: 400,
              height: 300,
            }}
            // @ts-ignore Stock type has `src` as optional
            data={image}
            alt={image.altText || `Picture of ${product.title}`}
            loading={loading}
          />
        )}
      </div>
      <div className={"flex-col space-y-6 md:space-y-8 h-full justify-between " + (reverseOrder ? "text-center md:items-end md:text-right" : "text-center md:items-start md:text-left")}>
        <div className="flex-col space-y-2">
          <h3 className="w-full pr-1 overflow-hidden whitespace-nowrap text-ellipsis heading-style text-primary text-2xl md:text-4xl">
            {product.title}
          </h3>
          <h3 className="w-full pr-1 overflow-hidden whitespace-nowrap text-ellipsis heading-style text-gray-300 text-md md:text-xl">
            {slogans[product.id?.toUpperCase()] ? slogans[product.id?.toUpperCase()] : ""}
          </h3>
        </div>
        <div className="">
          <Text className="">
            <Money withoutTrailingZeros data={price} />
            {isDiscounted(price, compareAtPrice) && (
              <CompareAtPrice
                className={'opacity-50'}
                data={compareAtPrice}
              />
            )}
          </Text>
        </div>
        <div>
          <Link to={`/products/${product.handle}`} className="accent-btn">
            Shop Now
          </Link>
          <Link to={`/products/about/${product.handle}`} className="black-btn">
            Learn more
          </Link>
        </div>
      </div>
    </div>
  );
}

function CompareAtPrice({data, className}) {
  const {currencyNarrowSymbol, withoutTrailingZerosAndCurrency} =
    useMoney(data);

  const styles = clsx('strike', className);

  return (
    <span className={styles}>
      {currencyNarrowSymbol}
      {withoutTrailingZerosAndCurrency}
    </span>
  );
}
